<template>
  <v-layout klass="container">
    <div class="row">
      <div class="col-md-10 offset-md-1 col-sm-12 pt-2 pb-2">
        <div class="card">
          <div class="card-body">
            <h3>{{ $t('Editing trip') }} {{ tripDisplayCode }}</h3><small> {{ $route.params.tripUid }} </small>
            <hr />
            <div class="text-center" v-if="!editReady">
              <b-spinner type="border"></b-spinner>
            </div>
            <fieldset v-show="currentStep===0 && editReady">
              <trip-form ref="originalTrip"
                         tripType="originalTrip"
                         v-model="stepReady[0]"
                         @update="updateStepData(0, $event)"
                         :originalTrip="trip.originalTrip"
              />
              <trip-form-options ref="originalTripOptions"
                                 tripType="originalTrip"
                                 v-model="stepReady[0]"
                                 @update="updateStepData(1, $event)"
                                 :originalTrip="trip.originalTrip"
              />
            </fieldset>

            <fieldset v-show="currentStep === lastStep">
              <template v-for="s in summaries">
                {{ s.addressHeader }}
                <h4 v-html="s.pickupAddress"></h4>
                <h5 class="mb-2">
                  <small><font-awesome-icon icon="arrow-right" class="mr-2"/></small>
                  <span v-html="s.dropoffAddress"></span>
                </h5>
                <div class="mb-4">
                  {{ s.scheduledDateHeader }}
                  <h5>{{ s.scheduled }}</h5>
                </div>
                <div class="mb-4">
                  <div class="radio" v-for="t in s.tariffs">
                    <label>
                      <input type="radio" :name="s.radioGroup" :value="t.tariff.tariffUid" v-model="tariff[s.radioGroup]">
                      <img :src="t.tariff.displayName.startsWith('Drechthopper') ? logo : wijkLogo">
                      <div style="display: inline-block">
                        <h5>{{ $t('Price') }}: € {{ t.amountCents | formatCents }}</h5>
                        {{ $t('Budget used') }} {{ Math.round(t.route.distanceInMeters/1000) }} km
                      </div>
                    </label>
                  </div>
                </div>
                <hr>
              </template>
              <div class="row mt-5 mb-5 d-none">
                <div class="col-sm-4">
                  <img :src="logo" class="w-100 mb-2"/>
                </div>
                <div class="col-sm-8">
                  <div class="text-center mt-3" v-if="calculation.inProgress">
                    <b-spinner variant="primary"></b-spinner>
                  </div>
                  <div v-else-if="calculation.error">
                    <div class="alert alert-danger" role="alert">
                      <i class="fa fa-exclamation-triangle"></i> {{ $t(calculation.error)}}
                    </div>
                    <h4 class="text-center"><a href="javascript:;" @click="calculatePrice">{{ $t('Try again') }}</a></h4>
                  </div>
                  <div class="row bg-light" v-else>
                    <div class="col-sm-7">
                      <h2 class="">{{ $t('Price') }}: € {{ calculationTotal.price }}</h2>
                      {{ $t('Budget used') }} {{ calculationTotal.budgetCost }} km
                    </div>
                    <div class="col-sm-5">
                      <a class="btn btn-lg btn-primary btn-block h-100"
                         style="line-height:3.2em"
                         @click="submitTrips"
                      >
                        {{ $t('Confirm') }}
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </fieldset>

            <div class="d-flex mb-3">
              <div>
                <a class="btn btn-secondary btn-lg"
                   v-show="currentStep===0"
                   @click="discardBooking">
                  {{ $t('Discard') }}
                </a>
                <!-- STEP BACK 1st STEP -->
                <a class="btn btn-secondary btn-lg"
                   @click="currentStep=0"
                   v-show="currentStep === lastStep"
                >
                  {{ $t('Change your trip') }}
                </a>
              </div>
              <div class="ml-auto">
                <!-- NEXT STEP BUTTON -->
                <button class="btn btn-lg pull-right"
                        @click="goNextStep"
                        v-show="currentStep!==stepsAvailable.length-1"
                        :disabled="!stepReady[currentStep] && !trip.tripUid"
                        :class="{'btn-outline-primary': !stepReady[currentStep], 'btn-primary': stepReady[currentStep]}"

                >
                  {{ $t('Next') }} <font-awesome-icon :icon="['far', 'arrow-alt-circle-right']"/>
                </button>
                <!-- LAST STEP calculation spinner and error -->
                <div v-if="currentStep === lastStep">
                  <div class="text-center font-weight-bold" v-if="calculation.inProgress" style="margin-top:10px;margin-right: 25px">
                    {{ $t('Tariff calculation in progress, please wait...')}}
                    <b-spinner variant="primary" style="vertical-align: middle; margin-left:10px"></b-spinner>
                  </div>
                  <div v-else-if="calculation.error">
                    <div class="alert alert-danger" role="alert" style="margin-bottom: 0">
                      <i class="fa fa-exclamation-triangle"></i> {{ $t(calculation.error)}}
                      <a href="javascript:;" @click="calculatePrice">{{ $t('Try again') }}</a>
                    </div>
                  </div>
                  <template v-else>
                    <div class="alert alert-info alert-price">
                      <span style="text-transform: uppercase">{{ $t('Total price') }}</span> <b>{{calculationTotal.price}}</b> <small>({{ $t('Budget used') }} {{calculationTotal.budgetCost}} km)</small>
                    </div>
                    <button class="btn btn-lg pull-right btn-primary"
                            @click="submitTrips"
                    >
                      {{ $t('Submit trip') }} <font-awesome-icon :icon="['far', 'arrow-alt-circle-right']"/>
                    </button>
                  </template>
                </div>
                <!-- SUBMIT BTN -->
                <button class="btn btn-lg pull-right btn-primary d-none"
                        @click="submitTrips"
                        v-show="currentStep === lastStep && !calculation.inProgress && !calculation.error"
                >
                  {{ $t('Submit trip') }} <font-awesome-icon :icon="['far', 'arrow-alt-circle-right']"/>
                </button>
              </div>
            </div>

          </div>
        </div>
      </div>
    </div>
  </v-layout>
</template>

<script>
  import VLayout from '@/layouts/Default';
  import TripForm from '@/components/TripForm';
  import TripFormOptions from '@/components/TripFormOptions';
  import Address from '@/models/Address';
  import logo from '../../../public/img/logo.png';
  import wijkLogo from '../../../public/img/wijk-tariff.png';
  import { mapGetters } from 'vuex';

  const Calculation= (data) => {
    const calculation= _.assignIn({
      responseCode: '', // valid 'OK'
      tariffSearchFilterCount: {
        byDebtor: 0,
        byDistance: 0,
        byPaymentType: 0,
        byPoiGeofence: 0,
        byPricePlan: 0,
        byScheduleDate: 0,
        byUserApi: 0
      },
      tariffs: []
    }, data);
    return JSON.parse(JSON.stringify(calculation));
  };

  const Tariff = (data) => {
    const tariff= _.assignIn({
      tariffUid: '',
      amountCents: '',
      isFixPriceTariff: false,
      currencyISOCode: '',
      vat: '',
      description: '',
      onlineCreditCardUid: ''
    }, data);
    return JSON.parse(JSON.stringify(tariff));
  }

  const Trip = (data) => {
    const trip = _.merge({
      tripNew: true,
      tripUid: '',
      externalTripId: '',
      tripData: {
        pickupEvent: {
          eventUid: '',
          eventLocation: new Address(),
          eventDateTime: ''
        },
        pickupEventDateTime: '',
        dropoffEvent: {
          eventUid: '',
          eventLocation: new Address(),
          eventDateTime: ''
        },
        dropoffEventDateTime: '',
        transportObjects: [],
        tagUids: [],
      },
      debtor: {
        debtorUid: null,
      },
      debtorUid: null,
      route: {
        distanceInMeters: '',
        timeInSeconds: '',
        polylineGeometry: ''
      },
      paymentType: '',
      returnForTripId: '',
      tariff: Tariff(),
    }, data);

    if( !trip.tripUid ) {
      trip.tripNew = true;
      trip.tripUid = short.uuid();
      trip.externalTripId = short.new();
    } else {
      trip.tripNew = false;
    }
    return trip;
  };

  export default {
    name: 'TripBookingEdit',

    components: {
      VLayout,
      TripForm,
      TripFormOptions
    },

    data() {
      return {
        logo,
        wijkLogo,
        currentStep: 0,
        withReturn: false,
        stepReady:[
          false, // original trip from
          true, // original trip options form
        ],
        steps: [
          {title: 'Editing trip', subtitle: this.$route.params.tripUid},
          {title: 'Tariff', subtitle: 'Price and summary'},
        ],

        tariffs: [],
        paymentType: 'ON_ACCOUNT',
        paymentTypes: ['ON_ACCOUNT', 'CASH'],
        trip: {
          originalTrip: Trip(),
        },
        externalTripId: '',
        tripDisplayCode: '',
        editReady: false,
        tariff: {
          originalTrip: ''
        },
        calculation: {
          xhr: null,
          inProgress: false,
          originalTrip: Calculation()
        },
      }
    },

    computed: {
      ...mapGetters('account', [
        'ICustomer',
        'customerAddresses',
        'customerDebtorUid',
        'customerDefaultPaymentType',
        'customerPhoneNumber',
      ]),
      stepsAvailable() {
        return this.steps.filter(e => this.withReturn || !e.forReturn)
      },
      lastStep() {
        return this.stepsAvailable.length - 1;
      },
      currentStepTitle() {
        return this.stepsAvailable[this.currentStep].title
      },
      currentStepSubtitle() {
        return this.stepsAvailable[this.currentStep].subtitle
      },
      summaries() {
        return [{
            addressHeader: this.$t('From') + ' - ' + this.$t('To'),
            scheduledDateHeader: this.$t('Scheduled time'),
            pickupAddress: this.trip.originalTrip.tripData.pickupEvent.eventLocation.formattedAddressHTML,
            dropoffAddress: this.trip.originalTrip.tripData.dropoffEvent.eventLocation.formattedAddressHTML,
            scheduled: this.$t(this.trip.originalTrip.isArrivalWarranty ? 'Dropoff' : 'Pickup') +': '+moment( this.trip.originalTrip.isArrivalWarranty ?
              this.trip.originalTrip.tripData.dropoffEventDateTime : this.trip.originalTrip.tripData.pickupEventDateTime )
              .format('L LT'),
            tariffs: this.calculation.originalTrip.tariffs,
            radioGroup: 'originalTrip'
        }]
      },
      calculationTotal() {
        let price = 0;
        let budgetCost = 0;
        //if (this.calculation.originalTrip.tariffs.length) {
        //  price += this.calculation.originalTrip.tariffs[0].amountCents
        //  budgetCost += this.calculation.originalTrip.tariffs[0].route.distanceInMeters
        //}

        const tariff= _.find(this.calculation.originalTrip.tariffs, e => e.tariff.tariffUid === this.tariff.originalTrip)
        if(tariff) {
          price+= tariff.amountCents
          budgetCost+= tariff.route.distanceInMeters
        }

        return {
          price: this.$options.filters.formatCents(price),
          budgetCost: Math.round(budgetCost/1000)
        }
      }
    },

    methods: {
      toggleReturn() {
        this.withReturn = !this.withReturn;
        this.currentStep = this.withReturn ? 2 : 1;
      },
      goBackStep() {
        if(this.currentStep > 0) {
          this.currentStep--;
        }
      },
      goNextStep() {
        if( this.currentStep < this.lastStep ) {
          this.currentStep++;
        }
      },
      updateStepData(step, data) {
        this.$log.debug(`Step data updated`, step, data)
        const {originalTrip}= this.getData();
        this.$set(this.trip, 'originalTrip', originalTrip)

      },
      stepsBeforeReady(step) {
        return _.every(this.stepReady, (ready, index)=>{
          return index < step ? ready : true
        })
      },
      getData() {
        const trips= {
          originalTrip: {}
        };
        _.forEach(trips, (val, key) =>{
          const formData = this.$refs[key].getData();
          const formOptionData= this.$refs[key+'Options'].getData();

          const tripData= _.assignIn({},
            formData.tripData,
            formOptionData.tripData,
          );

          const tags= _.uniq(_.compact([
            ...formData.tripData.tags,
            ...formOptionData.tripData.tags,
          ]))
          tripData.tags= tags.slice(0)
          tripData.tagUids= tags.slice(0)

          // auto add tags na zahtjev Demole i Antonija
          // https://intendanet.slack.com/archives/C6H66P2DT/p1698060008749699
          // https://bugtracker.intaxisolutions.com/view.php?id=11327
          _.forEach(this.$store.state.account.data.customerTagsWithAddType, tag =>{
            const {addType, tagUid}= tag
            if(addType === 'AUTO' && !_.includes(tags, tagUid)) {
              tripData.tags.push(tagUid)
              tripData.tagUids.push(tagUid)
              console.log(`Adding auto add tag`, tag)
            }
          })

          const customer= _.merge({},
            this.ICustomer,
            formData.customer,
            formOptionData.customer,
          );

          trips[key] = JSON.parse(JSON.stringify({
            agentUserUid: this.ICustomer.customerUid,
            returnForTripId: null,
            paymentExtras: [],
            paymentDiscountUids: [],
            tariff: {},
            debtor: {
              debtorUid: this.customerDebtorUid
            },
            debtorUid: this.customerDebtorUid,
            paymentType: this.customerDefaultPaymentType,
            tripData,
            customer,
            route: formData.route,
            isArrivalWarranty: formData.isArrivalWarranty
          }));
        });
        return trips;
      },
      selectTariffs() {
        const self= this
        const { tariffs } = this.calculation.originalTrip;
        if( tariffs && tariffs.length > 0) {
          const t= _.find(tariffs, e => e.tariff.tariffUid === this.tariff.originalTrip) || tariffs[0]
          const { tariff, amountCents, route, priceBreakdown } = JSON.parse(JSON.stringify(t))
          tariff.route = route;
          if( tariff.isFixPriceTariff ) {
            tariff.amountCents = amountCents;
          }
          this.$set(this.trip.originalTrip, 'tariff', tariff);
          this.tariff.originalTrip = tariff.tariffUid;

          if( priceBreakdown ) {
            const {amountPaymentExtras}= priceBreakdown
            if(amountPaymentExtras && amountPaymentExtras.length) {
              self.trip.originalTrip.paymentExtras= _.map(amountPaymentExtras, ({paymentExtraUid, quantity, amount})=>{
                return { paymentExtraUid, quantity, amount }
              })
            }
          }
        } else {
          // todo clear tariff ??
        }
      },
      calculatePrice() {
        const self = this;

        if( self.calculation.xhr ) {
          self.calculation.xhr.abort();
        }
        self.calculation.error = '';
        self.calculation.inProgress = true;
        self.$set(self.calculation, 'originalTrip', Calculation());
        self.$set(self.calculation, 'returnTrip', Calculation());

        // todo

        self.calculation.xhr = $.ajax({
          type: 'post',
          url: process.env.VUE_APP_API_LOCATION + '/api/trip/price',
          contentType: 'application/json',
          dataType: 'json',
          data: JSON.stringify(self.trip.originalTrip),
          headers: {
            Authorization: `Bearer ${localStorage.getItem('id_token')}`
          }
        })
          .done((res)=>{
            self.$set(self.calculation, 'originalTrip', Calculation(res));
          })
          .fail((jqXHR)=>{
            self.calculation.error = _.get(jqXHR, 'responseJSON.message')
          })
          .always(()=>{
            self.calculation.inProgress = false;
          });
      },
      submitTrips() {
        const self = this;
        self.$swal.fire({
          title: self.$t('Updating'),
          html: self.$t('Working, please wait...'),
          allowOutsideClick: false,
          allowEscapeKey: false,
          didOpen: () => {
            self.$swal.showLoading()
            self.$root.ajax('put', '/trips/'+self.externalTripId, self.trip.originalTrip)
            .done((res)=>{
              self.$swal.fire(
                self.$t('Success')+'!',
                self.$t('Trip updated') + `<h4>${self.tripDisplayCode}</h4>`,
                'success'
              ).then(()=>{
                self.$router.push('/home');
              })
            })
            .fail((jqXHR, textStatus, error)=>{
              self.$swal.fire(
                self.$t('Error')+'!',
                _.get(jqXHR, 'responseJSON.message', textStatus + " " + error),
                'error'
              )
            })
          }
        });
      },
      discardBooking() {
        const self= this;
        self.$swal.fire({
          title: self.$t('Discard'),
          text: self.$t('Are you sure you want to discard the changes?'),
          icon: 'question',
          showCancelButton: true,
          confirmButtonText: self.$t('Yes'),
          cancelButtonText: self.$t('No'),
        }).then((result) => {
          if (result.isConfirmed) {
            self.$router.push('/home');
          }
        })
      }
    },

    mounted() {
      const self = this;
      self.$root.ajax('get', '/trips/' + this.$route.params.tripUid)
      .done((res)=>{
        self.$set(self.trip, 'originalTrip', Trip(res))
        self.$nextTick(()=>{
          self.$refs.originalTrip.populateFromTrip()
          self.$refs.originalTripOptions.populateFromTrip()
          self.tripDisplayCode= res.tripDisplayCode;
          self.externalTripId= res.externalTripId;
          self.editReady = true;
        })

      })
      .fail((jqXHR, textStatus, errorThrown)=>{
        self.$swal.fire(
          self.$t('Error')+'!',
          self.$t('Failed to retrieve data') + `<h5>${_.get(jqXHR, 'responseJSON.message', errorThrown)}</h5>`,
          'error'
        ).then(()=>{
          self.$router.push('/home');
        })
      })
      if( process.env.NODE_ENV !== 'production' ) {
        // expose evil for dev purpose only!
        window._TripBookingEdit= this;
      }
    },

    watch: {
      stepReady(ready) {
        this.$log.info(`Steps ready`, ready);
      },
      currentStep(step) {
        if( step === this.lastStep ) {
          this.calculatePrice()
        }
      },
      calculation: {
        deep: true,
        handler() {
          if(!this.calculation.inProgress) {
            this.selectTariffs()
          }
        }
      },
      tariff: {
        deep: true,
        handler() {
          this.selectTariffs()
        }
      },
    }
  }
</script>

<style lang="scss"  scoped>
  .card-header-nav {
    padding: 0;
  }

  div.radio {
    border-top: 1px solid rgba(0, 0, 0, 0.1);
    > label {
      cursor: pointer;
      width: 100%;
      padding: 10px;
      margin-bottom: 0;
      > input {
        vertical-align: top;
        margin-top: 20px;
        margin-left: 15px;
        margin-right: 15px;
      }
      h5 {
        margin-bottom: 0;
      }
      img {
        vertical-align: top;
        height: 50px;
        margin-right: 20px;
      }
    }
    > label:hover {
      background: #f9f9f9;
    }
    > hr {

    }

  }

  .alert-price {
    display: inline-block;
    margin-right: 15px;
  }

</style>

